import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import PdpDealsBannerList from '@components/product/PdpDealsBannerList';
import getDealsBannerStickerTypes from '@components/product/getDealsBannerStickerTypes';
import getPdpDealsBannerData from '@adapters/prismic/fetching/pdpDealsBanner/getPdpDealsBannerData';
import {
  applyCodeDrivenSlot1Cap,
  getResolvedDealsBanners,
} from '@components/product/ProductPriceAndReviewsContainer/getResolvedDealsBanners';
import KlarnaProductPlacement from '@components/product/ProductBuyZone/Klarna/KlarnaProductPlacement';
import GiftCardForm from './GiftCardForm';
import ProductAddToCartButtonWrapper from './ProductAddToCartButtonWrapper';
import ProductAvailability from './ProductAvailability';
import NewProductAvailability from './MarketPlacerRedesign/ProductAvailability';
import ProductColourSelector from './ProductColourSelector';
import PdpColourPickerV2 from '_components/ColourPicker/PdpColourPickerV2';
import ProductFitSentiment from './ProductFitSentiment';
import ProductLowInventory from './ProductLowInventory';
import ProductQuantitySelector from './ProductQuantitySelector';
import ProductSizeSelector from './ProductSizeSelector';
import PromotionMessaging from '../ProductPromotionMessaging';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import ProductValueProposition from './ProductValueProposition';
import AlbyScrollButton from '@components/product/AlbyPDP/AlbyScrollButton';
import {
  FEATURE_FLAG_DEALS_BANNER,
  FEATURE_FLAG_DEALS_BANNER_SALE,
  FEATURE_FLAG_NEW_SPU_PROMOTION,
  FEATURE_FLAG_NEW_YEARS_PROMOTION,
  FEATURE_FLAG_OVERRIDE_PDP_VALUE_PROPS,
  FEATURE_FLAG_PDP_2026_1,
  FEATURE_FLAG_PDP_PRODUCT_AVAILABILITY,
  FEATURE_FLAG_COLOUR_PICKER_V2,
  marketplacerFFCookieName,
} from '@config/feature-flag';
import {
  MecProduct,
  MecVariant,
  PriceType,
} from '@adapters/bigcommerce/types/product';
import valuePropsEn from 'prismic-value-props-EN.json';
import valuePropsFr from 'prismic-value-props-FR.json';
import {
  FEATURE_FLAG_ALBY_CHATBOT,
  FEATURE_FLAG_MARKETPLACE,
} from '@config/feature-flag';

import {
  FitSizeValueInterface,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';

import { usePdp } from '@context/PdpContext';
import MarketplaceSellerInfo from '@components/product/ProductBuyZone/MarketplaceSellerInfo';
import {
  overrideValuePropsEn,
  overrideValuePropsFr,
  restrictedReturnValueProp,
  hasReturnRestriction,
} from '@utils/pdp/defaultPDPValueProps';
import { MEC_SELLER_NAME } from '@utils/constants';
import { resolvedBannerToTrackingLabel } from '@utils/gtm/ga4/utils';
import InStorePromotion from '_components/Product/InStorePromoMessage';

import styles from './ProductBuyZone.module.scss';
import Cookies from 'js-cookie';

interface ProductBuyZoneProps {
  product: MecProduct;
  productRelativePath: string;
  measureData: SizeChartInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  fitSentimentData: any;
  hasRestrictions: string;
  restrictions: MecProduct['shippingRestrictions'];
  productCode: string;
  abVariant: string | null;
  albyRef: React.RefObject<HTMLDivElement | null>;
}

interface MarketplaceInfo {
  shouldDisplayMarketplace: boolean;
  isMarketplace: boolean;
  marketplaceSellerName: string;
}

const getMarketplaceInfo = (
  selectedVariant: MecVariant | null | undefined,
  variants: MecVariant[] | null | undefined
): MarketplaceInfo => {
  // By default, we don't show the marketplace messaging... unless they've picked a variant already, or all the possible
  // variants are MEC, or all the variants are a marketplace seller

  const availableVariants =
    variants?.filter(
      (variant) => variant.hasStockAvailable && variant.availableForSaleOnline
    ) ?? [];
  const marketplaceVariants = availableVariants.filter(
    (variant) => variant.isMarketplace
  );

  // in this case, they have selected a variant now, so we can key the marketplace stuff off of that
  if (selectedVariant) {
    return {
      shouldDisplayMarketplace: true,
      isMarketplace: selectedVariant.isMarketplace,
      marketplaceSellerName:
        selectedVariant.marketplaceSellerName ?? MEC_SELLER_NAME,
    };
  }

  // If no available variants, don't display marketplace info
  if (availableVariants.length === 0) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: false,
      marketplaceSellerName: MEC_SELLER_NAME,
    };
  }

  // in this case, all variants are marketplace products
  // Assume they're all from the same seller. Take the first one's seller name.
  if (marketplaceVariants.length === availableVariants.length) {
    return {
      shouldDisplayMarketplace: true,
      isMarketplace: true,
      marketplaceSellerName:
        availableVariants[0].marketplaceSellerName ?? MEC_SELLER_NAME,
    };
  }

  // If there are marketplace variants, but no selected variant, don't display marketplace info
  if (marketplaceVariants.length > 0 && !selectedVariant) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: true,
      marketplaceSellerName:
        marketplaceVariants[0].marketplaceSellerName ?? 'MEC',
    };
  }

  // If there are marketplace variants, but no selected variant, don't display marketplace info
  if (marketplaceVariants.length > 0 && !selectedVariant) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: true,
      marketplaceSellerName:
        marketplaceVariants[0].marketplaceSellerName ?? 'MEC',
    };
  }

  // No variants are marketplace products OR mixed marketplace/MEC variants
  return {
    shouldDisplayMarketplace: true,
    isMarketplace: false,
    marketplaceSellerName: MEC_SELLER_NAME,
  };
};

const ProductBuyZone = ({
  product,
  productRelativePath,
  measureData,
  fitAndRise,
  fitSentimentData,
  hasRestrictions,
  restrictions,
  abVariant,
  albyRef,
}: ProductBuyZoneProps) => {
  const locale = useLocale();
  const { t } = useTranslation('common');
  const {
    brand,
    categoryTargets,
    price,
    actualPriceList,
    hasMemberPriceDifference,
    promoBadge,
    productCode,
    selectedAvailability,
    selectedColour,
    selectedVariant,
    variants,
  } = usePdp();
  const marketplaceEnabled =
    FEATURE_FLAG_MARKETPLACE ||
    Cookies.get(marketplacerFFCookieName) === 'true';

  const prismicValueProps = locale === 'en' ? valuePropsEn : valuePropsFr;
  const overrideValueProps =
    locale === 'en' ? overrideValuePropsEn : overrideValuePropsFr;

  let valueProps = FEATURE_FLAG_OVERRIDE_PDP_VALUE_PROPS
    ? overrideValueProps
    : prismicValueProps;

  if (hasReturnRestriction(product)) {
    const restrictedProp =
      locale === 'en'
        ? restrictedReturnValueProp.en
        : restrictedReturnValueProp.fr;
    valueProps = valueProps.map((prop) =>
      prop.iconName === 'valuepropsreturn'
        ? {
            ...prop,
            title: restrictedProp.title,
            linkUrl: restrictedProp.linkUrl,
          }
        : prop
    );
  }

  const { shouldDisplayMarketplace, marketplaceSellerName } =
    getMarketplaceInfo(selectedVariant, variants);

  const pdpDealsBannerEntries = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getPdpDealsBannerData(locale);
  }, [locale]);

  const allResolvedDealsBanners = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getResolvedDealsBanners(pdpDealsBannerEntries, {
      brand: brand?.name,
      categoryTargets,
      stickerTypes: getDealsBannerStickerTypes(promoBadge),
      style: selectedColour?.name,
      productCode,
      variantSku: selectedVariant?.sku ?? null,
      activePriceGroup: actualPriceList?.groupKey ?? null,
    });
  }, [
    actualPriceList?.groupKey,
    brand?.name,
    categoryTargets,
    pdpDealsBannerEntries,
    promoBadge,
    productCode,
    selectedColour?.name,
    selectedVariant?.sku,
  ]);

  const resolvedSlot2DealsBanners = useMemo(
    () => allResolvedDealsBanners.filter((banner) => banner.position === 2),
    [allResolvedDealsBanners]
  );

  const prismicDealsBannerTitles = useMemo(() => {
    const codeDrivenSlot1Count =
      (FEATURE_FLAG_PDP_2026_1 && price?.priceType === PriceType.Clearance
        ? 1
        : 0) +
      (hasMemberPriceDifference && price?.priceType !== PriceType.Clearance
        ? 1
        : 0) +
      (FEATURE_FLAG_DEALS_BANNER_SALE && !!price?.epochEndDate ? 1 : 0);
    const cappedSlot1 = applyCodeDrivenSlot1Cap(
      allResolvedDealsBanners.filter((b) => b.position === 1),
      codeDrivenSlot1Count
    );
    const slot2 = allResolvedDealsBanners.filter((b) => b.position === 2);
    return [...cappedSlot1, ...slot2]
      .map(resolvedBannerToTrackingLabel)
      .filter((label): label is string => label !== null);
  }, [
    allResolvedDealsBanners,
    price?.priceType,
    price?.epochEndDate,
    hasMemberPriceDifference,
  ]);

  return (
    <>
      {!product?.isGiftCard && !selectedAvailability && (
        <>
          {hasRestrictions && (
            <AlertInlineMessage
              messageType={restrictions.message_type}
              classNames={styles.itemAlert}
              key={restrictions.updated_at}
            >
              <p
                data-testid="top-buyzone-alert-message"
                dangerouslySetInnerHTML={{
                  __html:
                    locale !== 'fr'
                      ? restrictions.message_en
                      : restrictions.message_fr,
                }}
              />
            </AlertInlineMessage>
          )}
        </>
      )}
      {FEATURE_FLAG_COLOUR_PICKER_V2 ? (
        <PdpColourPickerV2
          path={productRelativePath}
          product={product}
          prismicDealsBannerTitles={prismicDealsBannerTitles}
        />
      ) : (
        <ProductColourSelector path={productRelativePath} product={product} />
      )}

      <ProductSizeSelector
        sizeDetail={product?.sizeData}
        measure={measureData}
        fitAndRise={fitAndRise}
        isBike={product?.isBike}
        brandName={product?.brand}
      />
      {product?.isGiftCard ? (
        <GiftCardForm />
      ) : (
        <>
          <ProductFitSentiment fitSentimentData={fitSentimentData} />
          {selectedAvailability && (
            <div className={styles.quantityAndLowInventory}>
              <ProductQuantitySelector />
              <div className={styles.lowInventoryAndAlby}>
                {!FEATURE_FLAG_PDP_PRODUCT_AVAILABILITY && (
                  <ProductLowInventory />
                )}
                {locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT && (
                  <AlbyScrollButton albyRef={albyRef} />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {FEATURE_FLAG_DEALS_BANNER && resolvedSlot2DealsBanners.length > 0 && (
        <div className={styles.dealsBannerContainer}>
          <PdpDealsBannerList
            banners={resolvedSlot2DealsBanners}
            containerClassName={styles.dealsBanner}
          />
        </div>
      )}

      {FEATURE_FLAG_NEW_SPU_PROMOTION && (
        <div className={styles.dealsBannerContainer}>
          <InStorePromotion />
        </div>
      )}

      {!product.isGiftCard && FEATURE_FLAG_PDP_PRODUCT_AVAILABILITY ? (
        <NewProductAvailability />
      ) : !product.isGiftCard ? (
        <ProductAvailability abVariant={abVariant} />
      ) : null}

      {shouldDisplayMarketplace && marketplaceEnabled && (
        <MarketplaceSellerInfo sellerName={marketplaceSellerName} />
      )}

      {selectedAvailability && (
        <ProductAddToCartButtonWrapper
          prismicDealsBannerTitles={prismicDealsBannerTitles}
        />
      )}
      {restrictions && restrictions.restriction_level !== 'R9' ? (
        <KlarnaProductPlacement
          placement="credit-promotion-auto-size"
          locale={locale}
          className={styles.klarna}
        />
      ) : (
        <br />
      )}
      {FEATURE_FLAG_NEW_YEARS_PROMOTION && (
        <PromotionMessaging
          startDate="2026-01-01 00:00"
          endDate="2026-01-01 23:59"
        />
      )}
      {!product?.isGiftCard && (
        <>
          {hasRestrictions &&
            selectedAvailability &&
            restrictions.restriction_level !== 'R4' && (
              <AlertInlineMessage
                messageType={restrictions.message_type}
                classNames={styles.itemAlert}
                key={restrictions.updated_at}
              >
                <p
                  data-testid="bottom-buyzone-alert-message"
                  dangerouslySetInnerHTML={{
                    __html:
                      locale !== 'fr'
                        ? restrictions.message_en
                        : restrictions.message_fr,
                  }}
                />
              </AlertInlineMessage>
            )}

          {valueProps.map((valueProp, index) => (
            <ProductValueProposition
              key={`valueProp-${valueProp.title}-${valueProp.linkUrl}`}
              title={valueProp.title}
              iconName={valueProp.iconName}
              linkUrl={valueProp.linkUrl}
              locale={locale ?? 'en'}
              position={index}
            />
          ))}

          {product.quebecRepairDisclosureFlag && (
            <div
              className={styles.qcTeaser}
              dangerouslySetInnerHTML={{ __html: t('qc.bill29.teaser') }}
            />
          )}
        </>
      )}
    </>
  );
};

export default ProductBuyZone;
